<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import {
  applyTenantBranding,
  clearTenantBranding,
} from 'shared/composables/useTenantBranding';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const DEFAULT_BRANDING = {
  primary_color: '#365E70',
  primary_hover_color: '#294956',
  foreground_color: '#FFFFFF',
  display_name: '',
};

const { t } = useI18n();
const store = useStore();
const { currentAccount } = useAccount();
const form = ref({ ...DEFAULT_BRANDING });
const logo = ref(null);
const logoDark = ref(null);
const removeLogo = ref(false);
const removeLogoDark = ref(false);
const isSaving = ref(false);

watch(
  currentAccount,
  account => {
    form.value = {
      ...DEFAULT_BRANDING,
      ...(account?.branding || {}),
    };
    logo.value = null;
    logoDark.value = null;
    removeLogo.value = false;
    removeLogoDark.value = false;
  },
  { deep: true, immediate: true }
);

const selectedFile = event => event.target.files?.[0] || null;

const brandingIsValid = () =>
  ['primary_color', 'primary_hover_color', 'foreground_color'].every(field =>
    /^#[0-9A-Fa-f]{6}$/.test(form.value[field])
  );

const saveBranding = async () => {
  if (!brandingIsValid()) {
    useAlert(t('GENERAL_SETTINGS.FORM.BRANDING.INVALID'));
    return;
  }

  const payload = new FormData();
  Object.entries(form.value).forEach(([key, value]) => {
    payload.append(`branding[${key}]`, value || '');
  });
  if (logo.value) payload.append('branding[logo]', logo.value);
  if (logoDark.value) payload.append('branding[logo_dark]', logoDark.value);
  payload.append('branding[remove_logo]', removeLogo.value);
  payload.append('branding[remove_logo_dark]', removeLogoDark.value);

  isSaving.value = true;
  try {
    const account = await store.dispatch('accounts/updateBranding', payload);
    applyTenantBranding(account.branding);
    useAlert(t('GENERAL_SETTINGS.FORM.BRANDING.API.SUCCESS'));
  } catch {
    useAlert(t('GENERAL_SETTINGS.FORM.BRANDING.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const resetBranding = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('accounts/resetBranding');
    clearTenantBranding();
    useAlert(t('GENERAL_SETTINGS.FORM.BRANDING.API.RESET_SUCCESS'));
  } catch {
    useAlert(t('GENERAL_SETTINGS.FORM.BRANDING.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.BRANDING.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.BRANDING.NOTE')"
    with-border
  >
    <form class="grid gap-4" @submit.prevent="saveBranding">
      <WithLabel
        name="tenant-display-name"
        :label="t('GENERAL_SETTINGS.FORM.BRANDING.DISPLAY_NAME')"
      >
        <NextInput
          v-model="form.display_name"
          type="text"
          maxlength="60"
          class="w-full"
        />
      </WithLabel>

      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <WithLabel
          name="tenant-primary-color"
          :label="t('GENERAL_SETTINGS.FORM.BRANDING.PRIMARY_COLOR')"
        >
          <NextInput v-model="form.primary_color" type="text" class="w-full" />
        </WithLabel>
        <WithLabel
          name="tenant-hover-color"
          :label="t('GENERAL_SETTINGS.FORM.BRANDING.HOVER_COLOR')"
        >
          <NextInput
            v-model="form.primary_hover_color"
            type="text"
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          name="tenant-foreground-color"
          :label="t('GENERAL_SETTINGS.FORM.BRANDING.FOREGROUND_COLOR')"
        >
          <NextInput
            v-model="form.foreground_color"
            type="text"
            class="w-full"
          />
        </WithLabel>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <WithLabel
          name="tenant-logo"
          :label="t('GENERAL_SETTINGS.FORM.BRANDING.LOGO')"
        >
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            class="block w-full text-sm"
            @change="logo = selectedFile($event)"
          />
          <label class="flex items-center gap-2 mt-2 text-sm">
            <input v-model="removeLogo" type="checkbox" />
            {{ t('GENERAL_SETTINGS.FORM.BRANDING.REMOVE_LOGO') }}
          </label>
        </WithLabel>
        <WithLabel
          name="tenant-dark-logo"
          :label="t('GENERAL_SETTINGS.FORM.BRANDING.DARK_LOGO')"
        >
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            class="block w-full text-sm"
            @change="logoDark = selectedFile($event)"
          />
          <label class="flex items-center gap-2 mt-2 text-sm">
            <input v-model="removeLogoDark" type="checkbox" />
            {{ t('GENERAL_SETTINGS.FORM.BRANDING.REMOVE_DARK_LOGO') }}
          </label>
        </WithLabel>
      </div>

      <div class="flex gap-2">
        <NextButton blue type="submit" :is-loading="isSaving">
          {{ t('GENERAL_SETTINGS.FORM.BRANDING.SAVE') }}
        </NextButton>
        <NextButton
          color="slate"
          variant="faded"
          type="button"
          :disabled="isSaving"
          @click="resetBranding"
        >
          {{ t('GENERAL_SETTINGS.FORM.BRANDING.RESET') }}
        </NextButton>
      </div>
    </form>
  </SectionLayout>
</template>
